import { Injectable, inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { OrderService } from './order.service';

/** Shape returned by `GET .../responders/me/work-context` and held as the client source of truth. */
export type ResponderWorkContext = {
  servingPolicies: any[];
  responderState: {
    activePolicyId?: string | null;
    allowedPolicyIds?: string[];
    policyVersion?: number | null;
    orderCopyPolicySyncPending?: boolean;
    orderCopyPolicySyncLastError?: string | null;
    orderCopyPolicySyncProcessed?: number | null;
    orderCopyPolicySyncTotal?: number | null;
    orderCopyPolicySyncSkipped?: number | null;
    /** Cloud Tasks task resource name while a policy sync is scheduled or running. */
    orderCopyPolicySyncCloudTaskName?: string | null;
    [key: string]: unknown;
  };
  /** Materialized policy workload count (PENDING/IN_PROGRESS + included in serving policy). */
  policyWorkload?: {
    count?: number;
    byQueue?: Record<string, number>;
    determinedAt?: string | null;
    stale?: boolean;
    recomputeScheduledAt?: string | null;
    retrievedAt?: string;
    recomputeFailedAt?: string | null;
    recomputeError?: string | null;
  };
};

function contextKey(workspaceId: string, sessionId: string): string {
  return `${workspaceId}:${sessionId}`;
}

/**
 * Single in-memory source of truth for responder work context for the active workspace+session.
 * All updates that change serving/policy state should flow through here (fetch after mutations or merge POST bodies)
 * so other tabs do not need to re-hit the API on every navigation.
 */
@Injectable({
  providedIn: 'root',
})
export class ResponderWorkContextService {
  private readonly orderService = inject(OrderService);

  private stored: { key: string; data: ResponderWorkContext } | null = null;

  /** Clears cached context (e.g. workspace/session shell changed). */
  clear(): void {
    this.stored = null;
  }

  getCached(workspaceId: string, sessionId: string): ResponderWorkContext | null {
    const k = contextKey(workspaceId, sessionId);
    if (!this.stored || this.stored.key !== k) {
      return null;
    }
    return this.stored.data;
  }

  /**
   * Replaces stored context from a full API payload (same shape as {@link OrderService.getResponderWorkContext}).
   */
  replace(workspaceId: string, sessionId: string, wc: ResponderWorkContext): void {
    this.stored = {
      key: contextKey(workspaceId, sessionId),
      data: {
        servingPolicies: wc?.servingPolicies ?? [],
        responderState: { ...(wc?.responderState ?? {}) },
        ...(wc?.policyWorkload != null ? { policyWorkload: wc.policyWorkload } : {}),
      },
    };
  }

  /**
   * Merges `responderState` from `setActivePolicy` (or similar) into the cache, keeping existing `servingPolicies`
   * unless `servingPoliciesOverride` is provided.
   */
  mergeResponderStateFromActivePolicyPost(
    workspaceId: string,
    sessionId: string,
    post: { responderState?: Record<string, unknown> },
    servingPoliciesOverride?: any[],
  ): void {
    const k = contextKey(workspaceId, sessionId);
    const prev = this.stored?.key === k ? this.stored.data : null;
    const policies = servingPoliciesOverride ?? prev?.servingPolicies ?? ([] as any[]);
    const nextRs = {
      ...(prev?.responderState ?? {}),
      ...(post?.responderState ?? {}),
    } as ResponderWorkContext['responderState'];
    this.stored = {
      key: k,
      data: {
        servingPolicies: policies,
        responderState: nextRs,
        ...(prev?.policyWorkload != null ? { policyWorkload: prev.policyWorkload } : {}),
      },
    };
  }

  /**
   * Fetches work context from the API, updates the store, and emits the result.
   */
  fetch(workspaceId: string, sessionId: string): Observable<ResponderWorkContext> {
    return this.orderService
      .getResponderWorkContext(workspaceId, sessionId)
      .pipe(tap((wc) => this.replace(workspaceId, sessionId, wc as ResponderWorkContext)));
  }
}
